import React, { useState } from "react";
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Paper,
  FormControlLabel,
  Checkbox,
  FormGroup,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Divider,
  Alert,
  CircularProgress,
  Snackbar,
  createTheme,
  ThemeProvider,
  Grid,
  FormHelperText
} from "@mui/material";

// Classic Windows XP-inspired theme for the public application page
const theme = createTheme({
  palette: {
    primary: { main: "#003399", light: "#0052cc", dark: "#002266" },
    secondary: { main: "#CC0000", light: "#ff3333", dark: "#990000" },
    background: { default: "#ECE9D8", paper: "#FFFFFF" },
  },
  typography: {
    fontFamily: '"Tahoma", "Segoe UI", sans-serif',
  },
  components: {
    MuiPaper: {
      styleOverrides: {
        root: {
          border: "1px solid #0054E3",
          boxShadow: "2px 2px 8px rgba(0, 0, 0, 0.2)",
        },
      },
    },
  },
});

// Experience options - when checked, show additional fields for details
const EXPERIENCE_OPTIONS = [
  { value: "roadside_assistance", label: "Roadside Assistance Experience" },
  { value: "towing", label: "Towing Experience" },
  { value: "ems_fire", label: "EMS/Fire Department Experience" },
  { value: "law_enforcement", label: "Law Enforcement Experience" },
  { value: "military", label: "Military Experience" },
  { value: "mechanic", label: "Mechanic Experience" },
  { value: "other_emergency", label: "Other Emergency Services Experience" },
];

const CERTIFICATION_OPTIONS = [
  { value: "cpr_first_aid", label: "CPR/First Aid Certified" },
  { value: "emt", label: "EMT/Paramedic" },
  { value: "firefighter", label: "TCFD Firefighter License" },
  { value: "tcole", label: "TCOLE Law Enforcement License" },
  { value: "cdl", label: "Commercial Driver's License (CDL)" },
  { value: "ham_radio", label: "Ham Radio License" },
  { value: "wrecker_operator", label: "TDLR Licensed Wrecker Operator" },
  { value: "responder_safety_learning_network", label: "Responder Safety Learning Network" },
  { value: "nims", label: "National Incident Management System (NIMS)" },
  { value: "ics", label: "Incident Command System" },
];

// Focused on roadside assistance equipment
const EQUIPMENT_OPTIONS = [
  { value: "jump_starter", label: "Jump Starter/Battery Pack" },
  { value: "jumper_cables", label: "Jumper Cables" },
  { value: "tire_inflator", label: "Tire Inflator/Air Compressor" },
  { value: "lug_wrench", label: "Lug Wrench" },
  { value: "hydraulic_jack", label: "Hydraulic Jack" },
  { value: "tire_plug_kit", label: "Tire Plug Kit" },
  { value: "tow_straps", label: "Tow Straps/Recovery Straps" },
  { value: "gas_can", label: "Gas Can" },
  { value: "safety_cones", label: "Safety Cones/Triangles" },
  { value: "emergency_warning_lights", label: "Emergency Warning Lights" },
  { value: "safety_vest", label: "Reflective Safety Vest" },
  { value: "flashlight", label: "High-Power Flashlight" },
  { value: "first_aid_kit", label: "First Aid Kit" },
  { value: "fire_extinguisher", label: "Fire Extinguisher" },
  { value: "basic_tools", label: "Basic Hand Tools" },
];

const FREQUENCY_OPTIONS = [
  { value: "few_times_week", label: "A few times per week" },
  { value: "weekends", label: "Weekends only" },
  { value: "few_times_month", label: "A few times per month" },
  { value: "occasionally", label: "Occasionally, when available" },
];

function Apply() {
  const [form, setForm] = useState({
    // Section 1 - Basic Information
    firstName: "",
    lastName: "",
    dateOfBirth: "",
    email: "",
    phone: "",
    city: "",
    state: "TX",
    primaryAvailabilityArea: "",
    
    // Section 2 - Experience & Training
    experience: [],
    experienceDetails: {}, // { experience_type: { agency: "", duration: "" } }
    certifications: [],
    otherCertifications: "",
    
    // Section 3 - Vehicle & Equipment
    vehicleYear: "",
    vehicleMake: "",
    vehicleModel: "",
    vehicleType: "",
    equipment: [],
    emergencyWarningLightsDetails: "",
    otherEquipment: "",
    canPerformTireChanges: false,
    canPerformPushPull: false,
    canDeliverFuel: false,
    canPerformLightMechanic: false,
    canPerformMajorMechanic: false,
    
    // Section 4 - Availability
    availabilityFrequency: "",
    
    // Section 5 - Situational Questions
    scenarioStranded: "",
    scenarioConflict: "",
    scenarioLimitations: "",
    scenarioCrashScene: "",
    
    // Section 6 - Personal Statement
    whyJoin: "",
    
    // Section 7 - Values & Acknowledgements
    agreesToValues: false,
    agreesToTraining: false,
    acknowledgedNoAI: false,
    
    // Section 8 - Signature
    typedSignature: "",
    signatureDate: new Date().toISOString().split("T")[0],
  });

  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");
  const [snackbar, setSnackbar] = useState({ open: false, message: "" });

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleExperienceChange = (value, checked) => {
    if (checked) {
      setForm((prev) => ({
        ...prev,
        experience: [...prev.experience, value],
        experienceDetails: {
          ...prev.experienceDetails,
          [value]: { agency: "", duration: "" }
        }
      }));
    } else {
      setForm((prev) => {
        const newDetails = { ...prev.experienceDetails };
        delete newDetails[value];
        return {
          ...prev,
          experience: prev.experience.filter((v) => v !== value),
          experienceDetails: newDetails
        };
      });
    }
  };

  const handleExperienceDetailChange = (expType, field, value) => {
    setForm((prev) => ({
      ...prev,
      experienceDetails: {
        ...prev.experienceDetails,
        [expType]: {
          ...prev.experienceDetails[expType],
          [field]: value
        }
      }
    }));
  };

  const handleArrayChange = (field, value, checked) => {
    if (checked) {
      setForm((prev) => ({ ...prev, [field]: [...prev[field], value] }));
    } else {
      setForm((prev) => ({ ...prev, [field]: prev[field].filter((v) => v !== value) }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);

    try {
      // Calculate age from date of birth
      const today = new Date();
      const birthDate = new Date(form.dateOfBirth);
      let age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
        age--;
      }

      if (age < 21) {
        setError("You must be at least 21 years of age to apply.");
        setSubmitting(false);
        return;
      }

      // Validation
      if (!form.acknowledgedNoAI) {
        setError("You must acknowledge the AI usage policy to submit your application.");
        setSubmitting(false);
        return;
      }

      if (!form.agreesToValues || !form.agreesToTraining) {
        setError("You must agree to all acknowledgements to submit your application.");
        setSubmitting(false);
        return;
      }

      if (!form.typedSignature) {
        setError("Please provide your electronic signature.");
        setSubmitting(false);
        return;
      }

      const warningLightsDetails = form.equipment.includes("emergency_warning_lights")
        ? form.emergencyWarningLightsDetails.trim()
        : "";
      const combinedOtherEquipment = [
        form.otherEquipment.trim(),
        warningLightsDetails ? `Emergency Warning Lights Details: ${warningLightsDetails}` : "",
      ]
        .filter(Boolean)
        .join("\n");

      const response = await fetch("/api/applications", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...form,
          experience: form.experience.join(", "),
          experienceDetails: JSON.stringify(form.experienceDetails),
          certifications: form.certifications.join(", "),
          equipment: form.equipment.join(", "),
          otherEquipment: combinedOtherEquipment,
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || "Failed to submit application");
      }

      setSubmitted(true);
    } catch (err) {
      setError(err.message || "An error occurred while submitting your application.");
    } finally {
      setSubmitting(false);
    }
  };

  // Success screen
  if (submitted) {
    return (
      <ThemeProvider theme={theme}>
        <Box sx={{ minHeight: "100vh", bgcolor: "background.default", py: 4 }}>
          <Container maxWidth="md">
            <Paper sx={{ p: 4, textAlign: "center" }}>
              <Typography variant="h4" sx={{ color: "primary.main", fontWeight: 700, mb: 2 }}>
                Application Submitted Successfully
              </Typography>
              <Typography variant="body1" sx={{ mb: 3 }}>
                Thank you for your interest in Texas Highway Rangers. Your application has been 
                received and will be reviewed by our leadership team. We will contact you via 
                email or phone within 5-7 business days regarding the status of your application.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                If you have any questions, please contact us through our official channels.
              </Typography>
            </Paper>
          </Container>
        </Box>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ minHeight: "100vh", bgcolor: "background.default", py: 4 }}>
        <Container maxWidth="md">
          {/* Header */}
          <Paper sx={{ p: 3, mb: 3, bgcolor: "primary.main", color: "white", textAlign: "center" }}>
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
              Texas Highway Rangers
            </Typography>
            <Typography variant="h6">
              Volunteer Membership Application
            </Typography>
          </Paper>

          <Paper sx={{ p: 4 }}>
            {/* Introduction */}
            <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.7 }}>
              Thank you for your interest in becoming a Texas Highway Rangers volunteer. Our organization 
              is built on the principles of <strong>Service Before Self</strong>, providing free roadside 
              assistance to stranded motorists across Texas highways.
            </Typography>
            
            <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.7 }}>
              We are looking for individuals who embody Texas values: integrity, reliability, and a genuine 
              desire to help others without expecting anything in return. As a Highway Ranger, you will be 
              representing our organization to the public, and we take that responsibility seriously.
            </Typography>

            <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.7 }}>
              Please complete this application honestly and thoroughly. All applicants must be at least
              <strong> 21 years of age</strong>.
            </Typography>

            {/* AI Warning Box */}
            <Alert 
              severity="error" 
              sx={{ 
                mb: 3, 
                "& .MuiAlert-message": { fontWeight: 600 },
                border: "2px solid",
                borderColor: "error.main"
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 700, mb: 1 }}>
                IMPORTANT: AI Content Policy
              </Typography>
              <Typography variant="body2">
                All responses must be written by you personally. Applications containing AI-generated 
                content (ChatGPT, Claude, Bard, Copilot, etc.) will result in <strong>immediate denial 
                and permanent ban</strong> from future applications. We can detect AI-generated content, 
                and we value authenticity above polished writing.
              </Typography>
            </Alert>

            <Divider sx={{ my: 3 }} />

            <form onSubmit={handleSubmit}>
              {/* Section 1 - Basic Information */}
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: "primary.main" }}>
                SECTION 1: BASIC INFORMATION
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="First Name"
                    value={form.firstName}
                    onChange={(e) => handleChange("firstName", e.target.value)}
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Last Name"
                    value={form.lastName}
                    onChange={(e) => handleChange("lastName", e.target.value)}
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Date of Birth"
                    type="date"
                    value={form.dateOfBirth}
                    onChange={(e) => handleChange("dateOfBirth", e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    required
                    helperText="You must be at least 21 years old to apply"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Email Address"
                    type="email"
                    value={form.email}
                    onChange={(e) => handleChange("email", e.target.value)}
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Phone Number"
                    value={form.phone}
                    onChange={(e) => handleChange("phone", e.target.value)}
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="City"
                    value={form.city}
                    onChange={(e) => handleChange("city", e.target.value)}
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="What county do you live in?"
                    value={form.primaryAvailabilityArea}
                    onChange={(e) => handleChange("primaryAvailabilityArea", e.target.value)}
                    placeholder="e.g., Harris, Travis, Dallas, Tarrant"
                    required
                  />
                </Grid>
              </Grid>

              <Divider sx={{ my: 3 }} />

              {/* Section 2 - Experience & Training */}
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: "primary.main" }}>
                SECTION 2: EXPERIENCE & TRAINING
              </Typography>

              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Select any relevant experience you have. For each selection, please provide details 
                about where you gained this experience and for how long.
              </Typography>

              <FormGroup sx={{ mb: 3 }}>
                {EXPERIENCE_OPTIONS.map((option) => (
                  <Box key={option.value} sx={{ mb: form.experience.includes(option.value) ? 2 : 0 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={form.experience.includes(option.value)}
                          onChange={(e) => handleExperienceChange(option.value, e.target.checked)}
                        />
                      }
                      label={option.label}
                    />
                    {form.experience.includes(option.value) && (
                      <Box sx={{ ml: 4, mt: 1, display: "flex", gap: 2, flexWrap: "wrap" }}>
                        <TextField
                          size="small"
                          label="Agency/Company/Organization"
                          value={form.experienceDetails[option.value]?.agency || ""}
                          onChange={(e) => handleExperienceDetailChange(option.value, "agency", e.target.value)}
                          sx={{ minWidth: 250 }}
                          placeholder="e.g., Houston Fire Dept, AAA, US Army"
                        />
                        <TextField
                          size="small"
                          label="Duration"
                          value={form.experienceDetails[option.value]?.duration || ""}
                          onChange={(e) => handleExperienceDetailChange(option.value, "duration", e.target.value)}
                          sx={{ minWidth: 150 }}
                          placeholder="e.g., 3 years, 2015-2020"
                        />
                      </Box>
                    )}
                  </Box>
                ))}
              </FormGroup>

              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                Certifications (check all that apply):
              </Typography>
              <FormGroup sx={{ mb: 2 }}>
                {CERTIFICATION_OPTIONS.map((option) => (
                  <FormControlLabel
                    key={option.value}
                    control={
                      <Checkbox
                        checked={form.certifications.includes(option.value)}
                        onChange={(e) => handleArrayChange("certifications", option.value, e.target.checked)}
                      />
                    }
                    label={option.label}
                  />
                ))}
              </FormGroup>
              <TextField
                fullWidth
                label="Other Certifications"
                value={form.otherCertifications}
                onChange={(e) => handleChange("otherCertifications", e.target.value)}
                helperText="List any other relevant certifications not mentioned above"
                sx={{ mb: 2 }}
              />

              <Divider sx={{ my: 3 }} />

              {/* Section 3 - Vehicle & Equipment */}
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: "primary.main" }}>
                SECTION 3: VEHICLE & EQUIPMENT
              </Typography>

              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Please provide information about the primary vehicle you would use for roadside assistance.
              </Typography>

              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Vehicle Year"
                    value={form.vehicleYear}
                    onChange={(e) => handleChange("vehicleYear", e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Vehicle Make"
                    value={form.vehicleMake}
                    onChange={(e) => handleChange("vehicleMake", e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Vehicle Model"
                    value={form.vehicleModel}
                    onChange={(e) => handleChange("vehicleModel", e.target.value)}
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>Vehicle Type</InputLabel>
                    <Select
                      value={form.vehicleType}
                      label="Vehicle Type"
                      onChange={(e) => handleChange("vehicleType", e.target.value)}
                    >
                      <MenuItem value="sedan">Sedan</MenuItem>
                      <MenuItem value="suv">SUV</MenuItem>
                      <MenuItem value="truck">Pickup Truck</MenuItem>
                      <MenuItem value="van">Van</MenuItem>
                      <MenuItem value="other">Other</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>

              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                Roadside Assistance Equipment (check all you currently own):
              </Typography>
              <FormGroup sx={{ mb: 2 }}>
                {EQUIPMENT_OPTIONS.map((option) => (
                  <FormControlLabel
                    key={option.value}
                    control={
                      <Checkbox
                        checked={form.equipment.includes(option.value)}
                        onChange={(e) => handleArrayChange("equipment", option.value, e.target.checked)}
                      />
                    }
                    label={option.label}
                  />
                ))}
              </FormGroup>

              {form.equipment.includes("emergency_warning_lights") && (
                <TextField
                  fullWidth
                  label="Emergency Warning Lights Details"
                  value={form.emergencyWarningLightsDetails}
                  onChange={(e) => handleChange("emergencyWarningLightsDetails", e.target.value)}
                  helperText="Please include light color(s), mounting location, and any other relevant details."
                  multiline
                  rows={2}
                  sx={{ mb: 2 }}
                />
              )}

              <TextField
                fullWidth
                label="Other Equipment"
                value={form.otherEquipment}
                onChange={(e) => handleChange("otherEquipment", e.target.value)}
                helperText="List any other roadside assistance equipment you have"
                multiline
                rows={2}
                sx={{ mb: 3 }}
              />

              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                Roadside Assistance Capabilities:
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Check the services you are able and willing to perform:
              </Typography>
              <FormGroup sx={{ mb: 2 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={form.canPerformTireChanges}
                      onChange={(e) => handleChange("canPerformTireChanges", e.target.checked)}
                    />
                  }
                  label="Tire changes (I have experience changing tires and have the necessary equipment)"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={form.canPerformPushPull}
                      onChange={(e) => handleChange("canPerformPushPull", e.target.checked)}
                    />
                  }
                  label="Push/Pull assistance (I have a capable vehicle and recovery straps for safely moving disabled vehicles)"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={form.canDeliverFuel}
                      onChange={(e) => handleChange("canDeliverFuel", e.target.checked)}
                    />
                  }
                  label="Fuel delivery (I have an approved fuel container and am comfortable transporting fuel)"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={form.canPerformLightMechanic}
                      onChange={(e) => handleChange("canPerformLightMechanic", e.target.checked)}
                    />
                  }
                  label="Light mechanic work (e.g., replacing a serpentine belt, topping off fluids, battery replacement)"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={form.canPerformMajorMechanic}
                      onChange={(e) => handleChange("canPerformMajorMechanic", e.target.checked)}
                    />
                  }
                  label="Major mechanic work (e.g., alternator replacement, starter replacement, other under-hood repairs)"
                />
              </FormGroup>

              <Divider sx={{ my: 3 }} />

              {/* Section 4 - Availability */}
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: "primary.main" }}>
                SECTION 4: AVAILABILITY
              </Typography>

              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>How often could you be available to assist?</InputLabel>
                <Select
                  value={form.availabilityFrequency}
                  label="How often could you be available to assist?"
                  onChange={(e) => handleChange("availabilityFrequency", e.target.value)}
                  required
                >
                  {FREQUENCY_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>
                  There is no minimum commitment required. We appreciate whatever time you can give.
                </FormHelperText>
              </FormControl>

              <Divider sx={{ my: 3 }} />

              {/* Section 5 - Situational Questions */}
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: "primary.main" }}>
                SECTION 5: SITUATIONAL QUESTIONS
              </Typography>

              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Please answer the following questions honestly. There are no "right" answers; we want 
                to understand how you think and approach situations.
              </Typography>

              <Box sx={{ mb: 3 }}>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                  Scenario 1
                </Typography>
                <Typography variant="body2" sx={{ mb: 1.5 }}>
                  You come across a stranded motorist who appears frustrated and upset. How would you approach the situation?
                </Typography>
                <TextField
                  fullWidth
                  value={form.scenarioStranded}
                  onChange={(e) => handleChange("scenarioStranded", e.target.value)}
                  multiline
                  rows={3}
                  required
                  placeholder="Your answer..."
                />
              </Box>

              <Box sx={{ mb: 3 }}>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                  Scenario 2
                </Typography>
                <Typography variant="body2" sx={{ mb: 1.5 }}>
                  While assisting a motorist, a police officer stops and begins questioning your authority to help or criticizes your methods. How do you handle this?
                </Typography>
                <TextField
                  fullWidth
                  value={form.scenarioConflict}
                  onChange={(e) => handleChange("scenarioConflict", e.target.value)}
                  multiline
                  rows={3}
                  required
                  placeholder="Your answer..."
                />
              </Box>

              <Box sx={{ mb: 3 }}>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                  Scenario 3
                </Typography>
                <Typography variant="body2" sx={{ mb: 1.5 }}>
                  You encounter a situation that is beyond your skill level or equipment capabilities. What do you do?
                </Typography>
                <TextField
                  fullWidth
                  value={form.scenarioLimitations}
                  onChange={(e) => handleChange("scenarioLimitations", e.target.value)}
                  multiline
                  rows={3}
                  required
                  placeholder="Your answer..."
                />
              </Box>

              <Box sx={{ mb: 3 }}>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                  Scenario 4
                </Typography>
                <Typography variant="body2" sx={{ mb: 1.5 }}>
                  What would you do if you arrived first at a crash scene?
                </Typography>
                <TextField
                  fullWidth
                  value={form.scenarioCrashScene}
                  onChange={(e) => handleChange("scenarioCrashScene", e.target.value)}
                  multiline
                  rows={3}
                  required
                  placeholder="Your answer..."
                />
              </Box>

              <Divider sx={{ my: 3 }} />

              {/* Section 6 - Personal Statement */}
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: "primary.main" }}>
                SECTION 6: PERSONAL STATEMENT
              </Typography>

              <Typography variant="body2" sx={{ mb: 1.5 }}>
                Why do you want to join Texas Highway Rangers? What motivates you to volunteer your time helping strangers?
              </Typography>
              <TextField
                fullWidth
                value={form.whyJoin}
                onChange={(e) => handleChange("whyJoin", e.target.value)}
                multiline
                rows={4}
                required
                sx={{ mb: 2 }}
                placeholder="Your answer..."
                helperText="Be genuine. We value authenticity over polished answers."
              />

              <Divider sx={{ my: 3 }} />

              {/* Section 7 - Values & Acknowledgements */}
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: "primary.main" }}>
                SECTION 7: VALUES & ACKNOWLEDGEMENTS
              </Typography>

              <Typography variant="body2" sx={{ mb: 2, lineHeight: 1.7 }}>
                Texas Highway Rangers is built on a foundation of <strong>Service Before Self</strong>. 
                We believe that true service means helping others without expectation of recognition, 
                reward, or personal gain. Our members represent the best of Texas values: integrity, 
                reliability, and genuine care for our fellow Texans.
              </Typography>

              <Typography variant="body2" sx={{ mb: 3, lineHeight: 1.7 }}>
                We are not looking for heroes seeking glory or individuals who need validation. We are 
                looking for humble servants who find fulfillment in knowing they made someone's day better, 
                even if no one ever knows their name.
              </Typography>

              <FormGroup>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={form.agreesToValues}
                      onChange={(e) => handleChange("agreesToValues", e.target.checked)}
                    />
                  }
                  label={
                    <Typography variant="body2">
                      I have read and understand the values of Texas Highway Rangers. I commit to representing 
                      the organization with integrity, professionalism, and humility in all interactions.
                    </Typography>
                  }
                />

                <FormControlLabel
                  control={
                    <Checkbox
                      checked={form.agreesToTraining}
                      onChange={(e) => handleChange("agreesToTraining", e.target.checked)}
                    />
                  }
                  label={
                    <Typography variant="body2">
                      I understand that if accepted, I will be required to complete orientation and training 
                      before assisting motorists as an official Texas Highway Ranger.
                    </Typography>
                  }
                />

                {/* AI Acknowledgement - Prominent */}
                <Box sx={{ 
                  bgcolor: "error.light", 
                  p: 1.5, 
                  borderRadius: 1, 
                  mt: 2,
                  border: "1px solid",
                  borderColor: "error.main"
                }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={form.acknowledgedNoAI}
                        onChange={(e) => handleChange("acknowledgedNoAI", e.target.checked)}
                        sx={{ color: "error.dark", "&.Mui-checked": { color: "error.dark" } }}
                      />
                    }
                    label={
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        I certify that all responses in this application are written by me personally, 
                        without the use of AI tools (ChatGPT, Claude, Bard, etc.). I understand that 
                        AI-generated responses will result in immediate denial and permanent ban from 
                        future applications.
                      </Typography>
                    }
                  />
                </Box>
              </FormGroup>

              <Divider sx={{ my: 3 }} />

              {/* Section 8 - Signature */}
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: "primary.main" }}>
                SECTION 8: SIGNATURE
              </Typography>

              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                By signing below, I confirm that:
              </Typography>
              <Box component="ul" sx={{ pl: 3, mb: 2 }}>
                <li>
                  <Typography variant="body2" color="text.secondary">
                    The information provided in this application is accurate and truthful
                  </Typography>
                </li>
                <li>
                  <Typography variant="body2" color="text.secondary">
                    I understand that submission does not guarantee acceptance
                  </Typography>
                </li>
                <li>
                  <Typography variant="body2" color="text.secondary">
                    I agree to uphold the values and standards of Texas Highway Rangers if accepted
                  </Typography>
                </li>
                <li>
                  <Typography variant="body2" color="text.secondary">
                    I wrote this application myself without AI assistance
                  </Typography>
                </li>
              </Box>

              <TextField
                fullWidth
                label="Electronic Signature (type your full legal name)"
                value={form.typedSignature}
                onChange={(e) => handleChange("typedSignature", e.target.value)}
                required
                sx={{ mb: 2 }}
                helperText="By typing your name, you are electronically signing this application"
              />

              <TextField
                fullWidth
                label="Date"
                type="date"
                value={form.signatureDate}
                onChange={(e) => handleChange("signatureDate", e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ mb: 3 }}
              />

              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              <Button
                type="submit"
                variant="contained"
                size="large"
                fullWidth
                disabled={submitting}
                sx={{ py: 1.5, fontSize: "1.1rem", fontWeight: 600 }}
              >
                {submitting ? <CircularProgress size={24} /> : "Submit Application"}
              </Button>
            </form>
          </Paper>

          <Typography variant="caption" color="text.secondary" sx={{ display: "block", textAlign: "center", mt: 2 }}>
            © {new Date().getFullYear()} Texas Highway Rangers. All rights reserved.
          </Typography>
        </Container>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          message={snackbar.message}
        />
      </Box>
    </ThemeProvider>
  );
}

export default Apply;
